import { Renderer } from "./engine";
import type { SceneObject } from "./engine";
import { Agent, orb, type OrbStateId } from "./orb";
import {
  drawRibbon,
  RIBBON_BRAND,
  RIBBON_GRAPHITE,
  type RibbonPalette,
  ribbonBody,
} from "./ribbon";

/**
 * How far an orb's canvas reaches past its box on each side, as a fraction
 * of the box: room for the spin's ribbon, which swings out past the body.
 */
export const ORB_OVERSCAN = 0.5;

/**
 * Every Ask Opus orb on the page draws through one shared WebGL2 canvas and
 * one animation loop. Each orb owns a 2D canvas; per frame the shared canvas
 * renders that orb and is copied into it. The loop runs only while at least
 * one orb is on screen and the tab is visible. With reduced motion there is
 * no loop at all: an orb draws one still frame when it mounts, resizes or
 * changes state.
 */

/**
 * brand: always the Ask Opus fill. graphite and pearl (the agents): soft
 * graphite, or a white orb with graphite eyes, at rest, easing into the Ask
 * Opus fill while the orb does anything (any state but idle) and back at
 * idle.
 */
export type OrbTone = "brand" | "graphite" | "pearl";

export type OrbOptions = {
  /** The shape (makeBot); the round Ask Opus orb by default. */
  object?: SceneObject;
  tone?: OrbTone;
  /** Hold one still frame, as under reduced motion (nav and header bots). */
  still?: boolean;
  /**
   * pearl only: a colour token for the body, e.g. "--color-agent-orb";
   * --color-agent-orb when omitted. Read once, at mount.
   */
  tint?: string;
  /**
   * brand only: the Ask Opus orb in another two-colour gradient (a cover
   * gradient's from and to, any CSS colours). Read once, at mount.
   */
  fill?: [string, string];
  /** brand only: a grainy photo texture on the fill. Read once, at mount. */
  texture?: OrbTexture;
  /** Hold the body still at rest (no bob or lean); busy states still move. */
  calm?: boolean;
  /** An edge that moves like a soft blob instead of a circle, 0..1. */
  wobble?: number;
};

export type OrbTexture = {
  /** Fine film grain, 0..~0.3. */
  grain: number;
  /** Streaky light/dark mottling, 0..~0.6. */
  mottle: number;
  /** How strongly the accent specks show, 0..1. */
  specks: number;
  /** The specks' colour, any CSS colour. */
  speck: string;
  /** Varies the pattern per orb. */
  seed?: number;
};

// How far the graphite orbs move toward their target colour per frame
// (about half a second to arrive at 60 fps).
const ACTIVE_EASE = 0.07;

export type OrbHandle = {
  /** Change state; the form the orb had becomes the afterimage. */
  set(state: OrbStateId): void;
  /** A graphite orb warms to the Ask Opus fill while warm, even at idle. */
  setWarm(warm: boolean): void;
  destroy(): void;
};

type Instance = {
  canvas: HTMLCanvasElement;
  /** The orb's own square inside the overscanned canvas, in canvas px. */
  size: number;
  inset: number;
  ctx: CanvasRenderingContext2D;
  agent: Agent;
  object: SceneObject;
  tone: OrbTone;
  /** 0 at rest .. 1 active: how far a graphite orb has warmed to the fill. */
  act: number;
  /** Held warm from outside (a hovered gallery bot). */
  warm: boolean;
  visible: boolean;
  /** Never animates: drawn as under reduced motion. */
  still: boolean;
  /** At rest the body holds still while its texture keeps moving. */
  calm: boolean;
  /** How blob-like its edge is, 0 for a circle. */
  wobble: number;
  /** Reduced motion or still: needs one still frame. */
  dirty: boolean;
  /** WebGL2 went away for good (a lost context that can't be rebuilt). */
  onNoGl?: () => void;
  /** The pearl's colours, 0..1 rgb: body, edge (a factor of the body), eyes. */
  pearl: PearlColours;
  /** The fill's stops, or null for Ask Opus's own. */
  fill: FillStops | null;
  /** The spin's ribbon in the fill's own colours (null: Ask Opus's). */
  ribbon: RibbonPalette | null;
  tex: { amount: [number, number, number, number]; speck: Rgb } | null;
};

type Rgb = [number, number, number];
type PearlColours = { body: Rgb; edge: Rgb; eye: Rgb };

const WHITE: Rgb = [1, 1, 1];
// What the shader used before the agent tokens: the fallback if one is missing.
const PEARL_EDGE: Rgb = [0.86, 0.865, 0.89];
const PEARL_EYE: Rgb = [0.16, 0.16, 0.19];

// A colour token resolved to 0..1 rgb: painted into one pixel, so any CSS
// colour (oklch included) comes back as sRGB.
let probe: CanvasRenderingContext2D | null = null;
const tokenRgb = (token: string | undefined, fallback = WHITE): Rgb => {
  if (!token) return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(token)
    .trim();
  if (!value) return fallback;
  probe ??= document.createElement("canvas").getContext("2d", {
    willReadFrequently: true,
  });
  if (!probe) return fallback;
  probe.clearRect(0, 0, 1, 1);
  probe.fillStyle = value;
  probe.fillRect(0, 0, 1, 1);
  const [r, g, b] = probe.getImageData(0, 0, 1, 1).data;
  return [r / 255, g / 255, b / 255];
};

// The agent tokens (global.css, Layer 3), resolved for this theme. The edge
// goes to the shader as a factor of the orb's own colour, so a tinted body
// greys toward its edge the same way the white one does.
const pearlColours = (tint: string | undefined): PearlColours => {
  const orb = tokenRgb("--color-agent-orb");
  const edge = tokenRgb("--color-agent-orb-edge", PEARL_EDGE);
  return {
    body: tint ? tokenRgb(tint, orb) : orb,
    edge: edge.map((c, i) =>
      Math.min(1.5, Math.max(0.5, c / Math.max(orb[i], 0.01))),
    ) as Rgb,
    eye: tokenRgb("--color-agent-orb-eye", PEARL_EYE),
  };
};
export type FillStops = { core: Rgb; mid: Rgb; edge: Rgb; deep: Rgb };

// Any CSS colour to 0..1 rgb, by painting it into the probe pixel.
const cssRgb = (value: string, fallback = WHITE): Rgb => {
  probe ??= document.createElement("canvas").getContext("2d", {
    willReadFrequently: true,
  });
  if (!probe) return fallback;
  probe.clearRect(0, 0, 1, 1);
  probe.fillStyle = value;
  probe.fillRect(0, 0, 1, 1);
  const [r, g, b] = probe.getImageData(0, 0, 1, 1).data;
  return [r / 255, g / 255, b / 255];
};

// A gradient's stops in the Ask Opus fill's proportions: its first colour is
// the mid, raised to a power for the richer core (#ADBDFF^1.6 is #859CFD) and
// much further for the deep shading; its second colour is the edge (the gold).
const fillStops = ([from, to]: [string, string]): FillStops => {
  const a = cssRgb(from);
  return {
    core: a.map((c) => c ** 1.6) as Rgb,
    mid: a,
    edge: cssRgb(to),
    deep: a.map((c) => 0.7 * c ** 7) as Rgb,
  };
};

// The spin's ribbon in a fill's colours, laid out like RIBBON_BRAND: its
// halo is the fill's edge (where Ask Opus has gold), its body the core and
// mid, and its shadow the core darkened about as much as Ask Opus's is.
const to255 = (c: Rgb): [number, number, number] =>
  c.map((v) => Math.round(255 * Math.max(0, Math.min(1, v)))) as [
    number,
    number,
    number,
  ];
export const ribbonFor = (f: FillStops): RibbonPalette => ({
  halo: to255(f.edge),
  dark: to255(f.core.map((v) => v * 0.6) as Rgb),
  base: to255(f.core),
  light: to255(f.mid),
});

const NO_PEARL: PearlColours = {
  body: WHITE,
  edge: PEARL_EDGE,
  eye: PEARL_EYE,
};

const now = () => performance.now() / 1000;

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

let gl: { canvas: HTMLCanvasElement; renderer: Renderer } | null = null;
let glFailed = false;
const instances = new Set<Instance>();
let raf = 0;
let observer: IntersectionObserver | null = null;
let listening = false;

const renderer = (): Renderer | null => {
  if (gl) return gl.renderer;
  if (glFailed) return null;
  try {
    const canvas = document.createElement("canvas");
    // A lost context (GPU reset, too many contexts) is rebuilt on the next frame.
    canvas.addEventListener("webglcontextlost", (e) => {
      e.preventDefault();
      gl = null;
    });
    gl = { canvas, renderer: new Renderer(canvas) };
    return gl.renderer;
  } catch {
    glFailed = true;
    return null;
  }
};

const draw = (inst: Instance, time: number) => {
  const r = renderer();
  if (!r || !gl) {
    inst.onNoGl?.();
    return;
  }
  const { size, inset } = inst;
  if (!size) return;
  if (gl.canvas.width < size || gl.canvas.height < size) {
    gl.canvas.width = Math.max(gl.canvas.width, size);
    gl.canvas.height = Math.max(gl.canvas.height, size);
  }
  r.begin(gl.canvas.width, gl.canvas.height);
  const pose = inst.agent.pose(time);
  const graphite = inst.tone === "graphite";
  const pearl = inst.tone === "pearl";
  if (graphite || pearl) {
    const target = inst.warm || inst.agent.state !== "idle" ? 1 : 0;
    inst.act = inst.agent.still
      ? target
      : inst.act + (target - inst.act) * ACTIVE_EASE;
  }
  r.brand = graphite ? 2 - inst.act : 1;
  r.whirl = graphite ? 1 : 0;
  r.pearl = pearl ? 1 - inst.act : 0;
  r.pearlTint = inst.pearl.body;
  r.pearlEdge = inst.pearl.edge;
  r.pearlEye = inst.pearl.eye;
  r.fill = inst.fill;
  r.tex = inst.tex;
  // The texture drifts with the clock; a still orb holds one pattern.
  // A still orb, or any under reduced motion, holds one pattern.
  r.texTime = inst.still || prefersReducedMotion() ? 0 : time;
  r.wobble = inst.wobble;
  r.drawPose(inst.object, pose, 0, 0, size);
  const { ctx } = inst;
  ctx.clearRect(0, 0, inst.canvas.width, inst.canvas.height);
  ctx.drawImage(gl.canvas, 0, 0, size, size, inset, inset, size, size);
  // The spin's ribbon: its back half outside the body, then its front half.
  const body = ribbonBody(pose.P, inset, inset, size);
  if (body) {
    const palette =
      (graphite || pearl) && inst.act < 0.5
        ? RIBBON_GRAPHITE
        : inst.ribbon ?? RIBBON_BRAND;
    // A wobbly edge sits in from the circle (engine.js: 5% to 14% at full
    // wobble); clip the back half at its average so it meets the body.
    const edgeInset = inst.wobble * 0.095;
    drawRibbon(ctx, body, pose.P, false, palette, edgeInset);
    drawRibbon(ctx, body, pose.P, true, palette);
  }
};

const shouldRun = () =>
  !document.hidden &&
  !prefersReducedMotion() &&
  [...instances].some((inst) => inst.visible && !inst.still);

const frame = () => {
  raf = 0;
  const time = now();
  const reduced = prefersReducedMotion();
  for (const inst of instances) {
    if (!inst.visible) continue;
    if (reduced || inst.still) {
      if (!inst.dirty) continue;
      inst.agent.still = true;
      inst.dirty = false;
    } else inst.agent.still = inst.calm && inst.agent.state === "idle";
    draw(inst, time);
  }
  schedule();
};

/** Start the loop if anything needs a frame; otherwise let it stop. */
const schedule = () => {
  if (raf) return;
  const pendingStill = [...instances].some(
    (inst) => inst.visible && inst.dirty,
  );
  if (shouldRun() || (pendingStill && !document.hidden))
    raf = requestAnimationFrame(frame);
};

const onVisibility = () => schedule();

const ensureListeners = () => {
  if (listening) return;
  listening = true;
  document.addEventListener("visibilitychange", onVisibility);
  observer =
    typeof IntersectionObserver === "function"
      ? new IntersectionObserver((entries) => {
          for (const entry of entries)
            for (const inst of instances)
              if (inst.canvas === entry.target) {
                inst.visible = entry.isIntersecting;
                inst.dirty = true;
              }
          schedule();
        })
      : null;
};

const dropListeners = () => {
  if (!listening || instances.size) return;
  listening = false;
  document.removeEventListener("visibilitychange", onVisibility);
  observer?.disconnect();
  observer = null;
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
};

/**
 * Mount an orb into `canvas` (sized by CSS; `size` is its CSS size in px).
 * Returns null when WebGL2 is unavailable, so the caller can show a fallback.
 */
export const mountOrb = (
  canvas: HTMLCanvasElement,
  size: number,
  state: OrbStateId,
  {
    object = orb,
    tone = "brand",
    still = false,
    tint,
    fill,
    texture,
    calm = false,
    wobble = 0,
  }: OrbOptions = {},
  onNoGl?: () => void,
): OrbHandle | null => {
  if (!renderer()) return null;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const px = Math.round(size * dpr);
  const inset = Math.round(px * ORB_OVERSCAN);
  canvas.width = canvas.height = px + 2 * inset;
  const inst: Instance = {
    canvas,
    size: px,
    inset,
    ctx,
    agent: new Agent(state, still || prefersReducedMotion()),
    object,
    tone,
    act: state === "idle" ? 0 : 1,
    warm: false,
    still,
    onNoGl,
    pearl: tone === "pearl" ? pearlColours(tint) : NO_PEARL,
    fill: fill ? fillStops(fill) : null,
    ribbon: fill ? ribbonFor(fillStops(fill)) : null,
    calm,
    wobble,
    tex: texture
      ? {
          amount: [
            texture.grain,
            texture.mottle,
            texture.specks,
            texture.seed ?? 0,
          ],
          speck: cssRgb(texture.speck),
        }
      : null,
    // Without an IntersectionObserver, treat every orb as on screen.
    visible: typeof IntersectionObserver !== "function",
    dirty: true,
  };
  instances.add(inst);
  ensureListeners();
  observer?.observe(canvas);
  schedule();
  return {
    set(next) {
      // Still or reduced motion: no change animation, straight to the still frame.
      if (still || prefersReducedMotion()) inst.agent.hold(now(), next, 0);
      else inst.agent.set(next, now());
      inst.dirty = true;
      schedule();
    },
    setWarm(warm) {
      inst.warm = warm;
      inst.dirty = true;
      schedule();
    },
    destroy() {
      observer?.unobserve(canvas);
      instances.delete(inst);
      dropListeners();
    },
  };
};
